<template>
  <section class="mobile-agent-navigation" :aria-label="label">
    <button
      type="button"
      class="mobile-agent-navigation-toggle"
      :aria-expanded="authenticated ? expanded : null"
      :aria-controls="authenticated ? 'mobile-sidebar-agent-options' : null"
      @click="toggle"
    >
      <span class="mobile-agent-navigation-icon" aria-hidden="true"><el-icon><ChatDotRound /></el-icon></span>
      <span class="mobile-agent-navigation-label">{{ label }}</span>
      <el-icon v-if="authenticated" class="mobile-agent-navigation-chevron" aria-hidden="true"><ArrowRight /></el-icon>
    </button>
    <div v-if="authenticated" id="mobile-sidebar-agent-options" v-show="expanded" class="mobile-agent-navigation-options">
      <div v-if="!authenticated" class="mobile-agent-navigation-status">
        <button type="button" class="mobile-agent-navigation-action" @click="$emit('request-login')">
          {{ $t('layout.layout.agent_login') }}
        </button>
      </div>
      <div v-else>
        <div v-if="agents.length" class="mobile-agent-navigation-list">
          <button
            v-for="agent in agents"
            :key="agent.id"
            type="button"
            class="mobile-agent-navigation-item"
            :class="{ 'is-current': isCurrentAgent(agent) }"
            :aria-current="isCurrentAgent(agent) ? 'page' : null"
            :aria-label="agent.agent_name"
            @click="$emit('select', agent)"
          >
            <span class="mobile-agent-navigation-icon" aria-hidden="true">
              <AgentMappedAvatar :agent="agent" :src="agent.avatar" alt="" :size="24" />
            </span>
            <span class="mobile-agent-navigation-name">{{ agent.agent_name }}</span>
          </button>
        </div>
        <p v-if="loading" role="status" class="mobile-agent-navigation-status">{{ $t('common.loading') }}</p>
        <p v-else-if="error" role="alert" class="mobile-agent-navigation-status">
          {{ $t('layout.layout.agent_load_failed') }}
          <button type="button" class="mobile-agent-navigation-action" @click="loadAgents">
            {{ $t('layout.layout.agent_retry') }}
          </button>
        </p>
        <p v-else-if="!agents.length" class="mobile-agent-navigation-status">
          {{ $t('layout.layout.agent_empty') }}
        </p>
        <button
          v-if="agents.length && !finished && !loading && !error"
          type="button"
          class="mobile-agent-navigation-action mobile-agent-navigation-more"
          @click="loadAgents"
        >{{ $t('layout.layout.agent_load_more') }}</button>
      </div>
    </div>
  </section>
</template>

<script>
import { getMyAgentList } from '@/api/pc/agent'
import AgentMappedAvatar from '@/components/agent/AgentMappedAvatar.vue'
import { ArrowRight, ChatDotRound } from '@element-plus/icons-vue'

export default {
  name: 'MobileAgentNavigation',
  components: { AgentMappedAvatar, ArrowRight, ChatDotRound },
  props: {
    label: { type: String, required: true },
    authenticated: Boolean,
    teamId: { type: [Number, String], default: null },
    userId: { type: [Number, String], default: null },
    activeAgentId: { type: [Number, String], default: null },
    activeCategory: { type: String, default: '' }
  },
  emits: ['select', 'request-login'],
  data() {
    return {
      expanded: false,
      agents: [],
      page: 1,
      pageSize: 12,
      loading: false,
      error: false,
      finished: false,
      requestId: 0,
      disposed: false
    }
  },
  watch: {
    authenticated() { this.resetAgents() },
    teamId() { this.resetAgents() },
    userId() { this.resetAgents() }
  },
  mounted() {
    this.resetAgents()
  },
  beforeUnmount() {
    this.disposed = true
    this.requestId += 1
  },
  methods: {
    toggle() {
      if (!this.authenticated) {
        this.$emit('request-login')
        return
      }
      this.expanded = !this.expanded
    },
    isCurrentAgent(agent) {
      if (this.activeAgentId) return String(agent.id) === String(this.activeAgentId)
      return Boolean(this.activeCategory && agent.agent_category === this.activeCategory &&
        this.agents.find(item => item.agent_category === this.activeCategory)?.id === agent.id)
    },
    resetAgents() {
      this.requestId += 1
      this.agents = []
      this.page = 1
      this.loading = false
      this.error = false
      this.finished = false
      if (this.authenticated) this.loadAgents()
    },
    async loadAgents() {
      if (!this.authenticated || this.loading || this.finished || this.disposed) return
      const id = ++this.requestId
      const teamId = this.teamId
      const userId = this.userId
      const page = this.page
      this.loading = true
      this.error = false
      try {
        const response = await getMyAgentList({ page, page_size: this.pageSize })
        if (this.disposed || id !== this.requestId || !this.authenticated ||
            teamId !== this.teamId || userId !== this.userId) return
        if (!Array.isArray(response?.data)) throw new Error('Invalid agent list response')
        const known = new Set(this.agents.map(agent => String(agent.id)))
        this.agents = this.agents.concat(response.data.filter(agent => {
          if (!agent?.id || known.has(String(agent.id))) return false
          known.add(String(agent.id))
          return true
        }))
        this.finished = response.data.length < this.pageSize
        this.page = page + 1
      } catch (error) {
        if (!this.disposed && id === this.requestId) {
          console.error('[mobile-navigation] Agent 列表加载失败:', error)
          this.error = true
        }
      } finally {
        if (!this.disposed && id === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-agent-navigation {
  min-width: 0;
  padding: 4px 0;
  color: var(--sidebar-text, #303442);
}
.mobile-agent-navigation-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 10px 8px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  &[aria-expanded='true'] .mobile-agent-navigation-chevron { transform: rotate(90deg); }
}
.mobile-agent-navigation-label {
  flex: 1 1 0;
  min-width: 0;
  overflow-wrap: anywhere;
}
.mobile-agent-navigation-chevron {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  color: var(--sidebar-muted, #5f687a);
  font-size: 14px;
}
.mobile-agent-navigation-options {
  min-width: 0;
  padding: 4px 0 0 12px;
}
.mobile-agent-navigation-list {
  display: grid;
}
.mobile-agent-navigation-item,
.mobile-agent-navigation-toggle,
.mobile-agent-navigation-action {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.mobile-agent-navigation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 10px 8px;
  box-sizing: border-box;
}
.mobile-agent-navigation-item:hover,
.mobile-agent-navigation-toggle:hover,
.mobile-agent-navigation-action:hover {
  background: var(--sidebar-hover, #f1f2f5);
}
.mobile-agent-navigation-item:active,
.mobile-agent-navigation-toggle:active,
.mobile-agent-navigation-action:active {
  background: var(--sidebar-pressed, #e8eaef);
}
.mobile-agent-navigation-item.is-current {
  background: var(--sidebar-selected-bg, #ede9fe);
  color: var(--sidebar-selected, #6d28d9);

  .mobile-agent-navigation-name { font-weight: 500; }
  .mobile-agent-navigation-icon { color: inherit; }
}
.mobile-agent-navigation-item:focus-visible,
.mobile-agent-navigation-toggle:focus-visible,
.mobile-agent-navigation-action:focus-visible {
  outline: 2px solid var(--sidebar-accent, #7c3aed);
  outline-offset: -2px;
}
.mobile-agent-navigation-icon {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--sidebar-icon, #4b5263);

  .el-icon { width: 16px; height: 16px; font-size: 16px; }
}
.mobile-agent-navigation-name {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
.mobile-agent-navigation-status {
  margin: 4px 8px;
  color: var(--sidebar-muted, #5f687a);
  font-size: 13px;
  line-height: 1.5;
}
.mobile-agent-navigation-action {
  padding: 8px;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.mobile-agent-navigation-more {
  display: block;
  width: 100%;
  margin: 4px 0;
}
</style>
